import "./index.scss";

import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import { InputAdornment, Skeleton } from "@mui/material";
import ModalBox from "components/Modal";
import InfoBlue from "components/shared/InfoBlockBlue";
import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { disableWeekends, formatDateToIso, formatLocaleDateString, getCurrencyData } from "helpers/utils";
import type { SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchECBCurrencyData } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";

interface ICurrencyRatesModal {
    /**
     * this state contains name of the currency : ex "EUR" + used to render display modal condition
     * */
    showCurrencyRate: string | null;
    close: VoidFunction;
    companyCurrency: string | undefined;
    setExchangeRates: any;
    setIsDirty: React.Dispatch<SetStateAction<boolean>>;
}

const CurrencyRatesModal = ({
    showCurrencyRate,
    close,
    companyCurrency,
    setExchangeRates,
    setIsDirty,
}: ICurrencyRatesModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { exchangeRate, activePeriod } = useAppSelector((state) => state.period);
    const [isRateLoading, setIsRateLoading] = useState<"idle" | "pending" | "succeed" | "rejected">("idle");
    const [currencyDate, setCurrencyDate] = useState<string>(formatDateToIso(new Date()));
    const currency = getCurrencyData(showCurrencyRate as string);

    useEffect(() => {
        setIsRateLoading("pending");
        dispatch(
            fetchECBCurrencyData({
                currency: showCurrencyRate as string,
                date: currencyDate,
                periodId: activePeriod as number,
            }),
        ).then(() => {
            setIsRateLoading("succeed");
        });
    }, [currencyDate]);

    const renderInputDate = (params: any) => {
        return (
            <StyledTextField
                {...params}
                fullWidth
                InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <InputAdornment position="end" className="start-adornment">
                            <div className="adornment-wrapper">
                                <span>{`${companyCurrency}/${currency?.code}`}</span>
                            </div>
                        </InputAdornment>
                    ),
                }}
            />
        );
    };

    const assignCurrencyValue = () => {
        const currencyInput: HTMLInputElement | null = document.querySelector(`#currency-field-${showCurrencyRate}`);
        const exchangeRateValue = `${exchangeRate}`;
        if (currencyInput) {
            currencyInput.value = exchangeRateValue;
            setExchangeRates((prev) => {
                const newExchangeRates = [...prev];
                const index = newExchangeRates.findIndex((rate) => rate.currency == showCurrencyRate);
                if (index == -1)
                    newExchangeRates.push({ currency: showCurrencyRate, rate: exchangeRateValue, error: false });
                else {
                    newExchangeRates[index].rate = exchangeRateValue;
                    newExchangeRates[index].error = false;
                }
                return newExchangeRates;
            });
        }
        setIsDirty(true);
        close();
    };

    return (
        <ModalBox
            open={!!showCurrencyRate}
            onClose={close}
            mainTitle={t("Settings.CurrencyPrice")}
            subTitle={`${t("Settings.EuroTo")} ${currency?.name}`}
        >
            <div className="currency-rates-modal">
                <InfoBlue info={t("Settings.ExchangeInfo")} />
                <div className="currency-date-picker-wrapper">
                    <StyledDatePicker
                        shouldDisableDate={disableWeekends}
                        inputFormat="dd/MM/yyyy"
                        className="currency-date-picker"
                        value={currencyDate}
                        onChange={(date) => setCurrencyDate(formatDateToIso(date))}
                        renderInput={renderInputDate}
                        disableFuture
                    />
                </div>
                <div className="currency-result-wrapper">
                    <div className="currency-result-head">
                        <span>{`${t("Settings.ConversionRate")} ${formatLocaleDateString({
                            date: currencyDate,
                        })}`}</span>
                    </div>
                    <div className="currency-result-bottom" style={!exchangeRate ? { paddingTop: "0.75rem" } : {}}>
                        {!exchangeRate && <span className="no-data-found">{t("Settings.NoDataFound")}</span>}
                        <div className="currency-result">
                            <span className="currency-result-label">{`1.00 Euro - ${t("Settings.Equal")}`}</span>
                            <div className="currency-result-value">
                                {isRateLoading !== "succeed" ? (
                                    <Skeleton width={"70%"} />
                                ) : (
                                    <span>{exchangeRate || "-"}</span>
                                )}
                                <span>{currency?.name}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <StyledButton
                    disabled={isRateLoading !== "succeed" || !exchangeRate}
                    buttonType="primary"
                    onClick={assignCurrencyValue}
                >
                    {t("Settings.AssignValue")}
                </StyledButton>
            </div>
        </ModalBox>
    );
};

export default CurrencyRatesModal;
